import { visualPriority, locationFirst, eventSentence } from "@/components/visualV2/presentation";
// SUPERFICIE GLOBAL DE ALERTAS — visible en CUALQUIER ruta autenticada (Central, Radar, Bitácora,
// Persona, Sitios, Usuarios). Representa las alarmas ACTIVAS reales del backend. Nunca es un toast
// que desaparece solo. Distingue salud del canal (conectado / reconectando / sin conexión).
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAlerts } from "@/context/AlertsContext";
import * as sysNotify from "@/lib/systemNotify";
import { ESTADO_LABEL } from "@/lib/alarmMeta";
import { verboEvento, esTecnica, elapsedHuman } from "@/lib/consolaV3/meta";
import {
  Bell, BellRing, ChevronDown, ChevronUp, Wifi, WifiOff, Loader2, ArrowRight,
  Volume2, VolumeX, BellOff, ShieldCheck, MapPin, User, CheckCircle2, FlaskConical,
  Minimize2, Maximize2, BellPlus, BellMinus, Wrench, CircleAlert, TriangleAlert,
} from "lucide-react";

const useNow = () => {
  const [n, s] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []);
  return n;
};

function personaLabel(a) {
  if (a.persona_nombre) return a.persona_nombre;
  if (esTecnica(a)) return a.sitio_nombre || "Incidencia técnica";
  return "Atribución compartida / por sitio"; // persona_id null: NUNCA se inventa persona
}
function lugarLabel(a) {
  return locationFirst(a.zona_nombre, a.sitio_nombre) || "Ubicación no especificada";
}

function AlertCard({ a, nowMs, atenuada, cerrada, onGoto }) {
  const pm = visualPriority(a.prioridad);
  const tec = esTecnica(a);
  return (
    <div data-testid={`global-alert-item-${a.id}`} data-priority={pm.key}
      className={`gv2-card p-3 ${cerrada ? "gv2-closed" : ""}`}>
      <div className="gv2-card-head">
        <span className="gv2-card-labels">
          <span className="gv2-priority-badge">{pm.key === "alta" ? <CircleAlert aria-hidden="true" /> : <Bell aria-hidden="true" />}{pm.label}</span>
          <span className="gv2-state">{cerrada ? "Cerrada" : a.estado === "recibida" ? "Sin tomar" : ESTADO_LABEL[a.estado] || a.estado}</span>
        </span>
        <span className="gv2-time-block"><span className="gv2-time-label">Hace</span><span className="gv2-timer">{elapsedHuman(a.hora_recepcion_servidor, nowMs)}</span></span>
      </div>
      <p className="gv2-title mt-1.5">{eventSentence(verboEvento(a.tipo))}</p>
      <p className="gv2-person gv2-info-line mt-2">
        {tec ? <Wrench className="h-3.5 w-3.5 shrink-0" /> : <User className="h-3.5 w-3.5 shrink-0" />} {personaLabel(a)}
      </p>
      <p className="gv2-meta gv2-info-line gv2-location mt-1">
        <MapPin className="h-3 w-3 shrink-0" /> {lugarLabel(a)}
      </p>
      <div className="gv2-card-foot"><span className="gv2-type">{tec ? "Incidencia técnica" : "Persona"}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        {cerrada ? (
          <span data-testid={`global-alert-cerrada-${a.id}`} className="gv2-state">
            <CheckCircle2 className="h-3 w-3" /> Cerrada{a._cerrada_por ? ` · ${a._cerrada_por}` : ""}
          </span>
        ) : (
          <>
            {atenuada && a.operador_nombre && <span className="gv2-meta">{a.operador_nombre}</span>}
            <button data-testid={`global-alert-goto-${a.id}`} onClick={() => onGoto(a)}
              className="gv2-primary inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold">
              Ir al caso <ArrowRight className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function GlobalAlertLayer() {
  const nowMs = useNow();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    health, altaSinTomar, mediaSinTomar, enAtencion, baja, closing, isDemo,
    audioEnabled, enableAudio, audioState, resumeAudio, muteActivo, muteCurrent, clearMute,
    permisoNotif, solicitarPermisoNotif, notificacionesSoportadas,
  } = useAlerts();
  // Cada carga/sesión inicia COLAPSADO. Sólo el usuario abre el panel: una ALTA nueva actualiza
  // contador, badge, pulso y sonido, pero NUNCA autoabre ni roba la navegación.
  const [open, setOpen] = useState(false);
  // Compactar es SÓLO presentación (posición segura en móvil/tablet): conserva conteo y severidad.
  const [compacto, setCompacto] = useState(false);

  // Al cambiar de ruta (menú, logo Home, deep-link o "Ir al caso") el panel se colapsa para no
  // quedar encima del contenido nuevo. Abrir/cerrar es SÓLO estado de UI: no muta alarmas,
  // ownership, prioridad, conteos ni Bitácora.
  useEffect(() => { setOpen(false); }, [pathname]);

  const hayAlta = altaSinTomar.length > 0;
  const hayMedia = mediaSinTomar.length > 0;
  const confirmable = health === "conectado" || health === "demo";

  const onGoto = (a) => { setOpen(false); navigate(`/consola-v3?caso=${a.id}`); };

  // El click en la notificación del sistema reutiliza EXACTAMENTE el destino de "Ir al caso":
  // sólo navega. No ejecuta Take, no cambia ownership ni estado.
  useEffect(() => {
    sysNotify.setOnActivate((id) => { setOpen(false); navigate(`/consola-v3?caso=${id}`); });
    return () => sysNotify.setOnActivate(null);
  }, [navigate]);

  const muteLeft = muteActivo ? Math.max(0, Math.ceil((muteActivo.expira - nowMs) / 1000)) : 0;
  const muteMMSS = `${String(Math.floor(muteLeft / 60)).padStart(2, "0")}:${String(muteLeft % 60).padStart(2, "0")}`;

  const healthMeta = useMemo(() => {
    if (health === "demo") return { txt: "DEMO · datos ficticios", cls: "bg-[#9B2C2C]/15 text-[#9B2C2C] dark:bg-[#9B2C2C]/30 dark:text-[#F2B8B8]", Icon: FlaskConical };
    if (health === "sin_conexion") return { txt: "Sin conexión — estado no confirmable", cls: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200", Icon: WifiOff };
    if (health === "reconectando") return { txt: "Reconectando… última info", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200", Icon: Loader2 };
    return { txt: "Conectado", cls: "gv2-connected", Icon: Wifi };
  }, [health]);

  // Punto/estado del pill: nunca "verde tranquilo" cuando el estado no es confirmable.
  const pillTone = !confirmable
    ? "gv2-pill-unknown"
    : hayAlta
      ? "gv2-pill-alta"
      /* Contraste técnico: `text-white` sobre ámbar daba 2.15:1 (ilegible). Mismo ámbar
         semántico, etiqueta oscura: 9.5:1. */
      : hayMedia
        ? "gv2-pill-media"
        : "gv2-pill-idle";

  return (
    <div className="gv2">
      {/* Línea global de borde superior: señal inequívoca en TODAS las rutas. */}
      {confirmable && hayAlta && (
        <div data-testid="global-alert-topbar" className="fixed inset-x-0 top-0 z-[60] h-1.5 bg-[#B42318] pointer-events-none" />
      )}
      {confirmable && !hayAlta && hayMedia && (
        <div data-testid="global-alert-topbar" className="fixed inset-x-0 top-0 z-[60] h-1 bg-[#FF9500] pointer-events-none" />
      )}
      {!confirmable && (
        <div data-testid="global-alert-topbar" className="fixed inset-x-0 top-0 z-[60] h-1.5 bg-amber-400 animate-pulse pointer-events-none" />
      )}

      {/* Centro de alertas flotante. V2.1 · el muelle NO intercepta toques fuera de sus controles
          (pointer-events) y vive en z-[45]: sigue visible en la navegación normal, pero queda por
          debajo de modales, drawers y menú lateral (z-50) en vez de taparlos. El contenido
          desplazable reserva su carril inferior (.gv2-dock-space). */}
      <div className="gv2-dock fixed bottom-3 left-3 right-3 z-[45] flex flex-col items-end gap-2 sm:left-auto sm:bottom-4 sm:right-4">
        {open && (
          <div data-testid="global-alert-panel" className="w-full sm:w-[380px] max-w-full max-h-[60vh] sm:max-h-[70vh] overflow-hidden flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 px-3.5 py-2.5">
              <span className="text-sm font-extrabold text-[#012472] dark:text-slate-100">Alertas activas</span>
              <span data-testid="global-alert-health" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${healthMeta.cls}`}>
                <healthMeta.Icon className={`h-3 w-3 ${health === "reconectando" ? "animate-spin" : ""}`} /> {healthMeta.txt}
              </span>
            </div>

            {isDemo && (
              <div data-testid="global-alert-demo-strip" className="border-b border-[#9B2C2C]/30 bg-[#9B2C2C]/10 dark:bg-[#9B2C2C]/25 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#9B2C2C] dark:text-[#F2B8B8]">
                DEMOSTRACIÓN · DATOS FICTICIOS · NO SE MUESTRA MONITOREO REAL
              </div>
            )}

            {/* Control de sonido / silencio temporal */}
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-3.5 py-2">
              {audioState === "blocked" ? (
                <button data-testid="global-alert-enable-audio" onClick={resumeAudio}
                  className="gv2-chip-btn inline-flex items-center text-xs">
                  <TriangleAlert className="h-3.5 w-3.5 gv2-amber-text" /> Sonido bloqueado · activar
                </button>
              ) : !audioEnabled ? (
                <button data-testid="global-alert-enable-audio" onClick={enableAudio}
                  className="gv2-primary inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold">
                  <Volume2 className="h-3.5 w-3.5" /> Activar sonido
                </button>
              ) : muteActivo ? (
                <div className="flex w-full items-center justify-between gap-2">
                  <span data-testid="global-alert-mute-indicator" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-600 dark:text-slate-300">
                    <BellOff className="h-3.5 w-3.5" /> Silencio {muteMMSS}
                  </span>
                  <button data-testid="global-alert-unmute-btn" onClick={clearMute} className="text-xs font-semibold text-[#0891A6] hover:underline">Reactivar</button>
                </div>
              ) : (
                <button data-testid="global-alert-mute-btn" onClick={() => muteCurrent(5)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700">
                  <VolumeX className="h-3.5 w-3.5" /> Silenciar alertas actuales · 5 min
                </button>
              )}

              {/* Activación CONTROLADA de las notificaciones del sistema: sólo por gesto del
                  operador y una sola vez. Si el navegador las niega, la consola sigue igual. */}
              {notificacionesSoportadas && permisoNotif === "default" && (
                <button data-testid="global-alert-enable-notif" onClick={solicitarPermisoNotif}
                  title="Avisar con una notificación del sistema cuando Galarya no esté al frente"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#012472]/10 px-2.5 py-1 text-xs font-bold text-[#012472] hover:bg-[#012472]/20 dark:bg-slate-800 dark:text-slate-200">
                  <BellPlus className="h-3.5 w-3.5" /> Avisar fuera de Galarya
                </button>
              )}
              {notificacionesSoportadas && permisoNotif === "granted" && (
                <span data-testid="global-alert-notif-on" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <BellPlus className="h-3.5 w-3.5" /> Avisos fuera de Galarya activos
                </span>
              )}
              {notificacionesSoportadas && permisoNotif === "denied" && (
                <span data-testid="global-alert-notif-denied" title="Se habilita en los permisos del navegador para este sitio"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <BellMinus className="h-3.5 w-3.5" /> Avisos del sistema bloqueados por el navegador
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2.5 space-y-2">
              {!confirmable && (
                <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 p-3 text-center text-xs font-semibold text-amber-800 dark:text-amber-200">
                  No se puede confirmar el estado del centro. Mostrando la última información recibida.
                </div>
              )}
              {confirmable && !hayAlta && !hayMedia && enAtencion.length === 0 && closing.length === 0 && (
                <div data-testid="global-alert-empty" className="flex flex-col items-center gap-1.5 py-6 text-center">
                  <ShieldCheck className="h-8 w-8 text-emerald-400" />
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Sin alertas activas</p>
                  <p className="text-[11px] text-slate-400">Canal confirmado · operación normal</p>
                </div>
              )}
              {altaSinTomar.map((a) => <AlertCard key={a.id} a={a} nowMs={nowMs} onGoto={onGoto} />)}
              {mediaSinTomar.map((a) => <AlertCard key={a.id} a={a} nowMs={nowMs} onGoto={onGoto} />)}
              {closing.map((a) => <AlertCard key={`cerrada-${a.id}`} a={a} nowMs={nowMs} cerrada onGoto={onGoto} />)}
              {enAtencion.length > 0 && (
                <>
                  <p className="px-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">En atención</p>
                  {enAtencion.map((a) => <AlertCard key={a.id} a={a} nowMs={nowMs} atenuada onGoto={onGoto} />)}
                </>
              )}
              {baja.length > 0 && (
                <div data-testid="global-alert-baja-count" className="rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-1.5 text-center text-[11px] font-semibold text-slate-400">
                  {baja.length} evento(s) de prioridad baja — ver Bitácora
                </div>
              )}
            </div>
          </div>
        )}

        {/* AVISO DE AUDIO · distingue SILENCIADO POR EL OPERADOR de BLOQUEADO POR EL NAVEGADOR.
            El bloqueado es persistente (aunque no haya casos) y se resuelve con el propio gesto de
            tocarlo; sólo desaparece cuando el contexto confirma que ya reproduce. */}
        {(audioState === "blocked" || (audioState === "muted" && (hayAlta || hayMedia))) && (
          <div data-testid="gv2-audio-warning" data-audio-estado={audioState} role="status"
            className="gv2-audio-warning flex max-w-full items-center gap-2 self-end sm:max-w-[380px]">
            {audioState === "blocked" ? <TriangleAlert className="h-4 w-4 shrink-0 gv2-amber-text" /> : <VolumeX className="h-4 w-4 shrink-0 gv2-amber-text" />}
            {audioState === "blocked" ? (
              <button type="button" data-testid="gv2-audio-desbloquear" className="min-w-0 text-left font-semibold underline"
                onClick={async () => { const ok = await resumeAudio(); if (!ok) setOpen(true); }}>
                Sonido bloqueado — toca para activar
              </button>
            ) : (
              <span className="min-w-0">
                <span className="hidden sm:inline">Sonido silenciado por el operador · hay alertas pendientes. </span>
                <span className="sm:hidden">Sonido silenciado · </span>
                <button type="button" className="underline font-semibold" onClick={() => setOpen(true)}>
                  <span className="hidden sm:inline">Ver controles de sonido</span><span className="sm:hidden">Activar</span>
                </button>
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <button data-testid="global-alert-pill" onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={`Centro de alertas: ${altaSinTomar.length + mediaSinTomar.length} alerta(s) sin tomar (${altaSinTomar.length} de prioridad alta, ${mediaSinTomar.length} media). Canal: ${healthMeta.txt}. ${open ? "Cerrar" : "Abrir"} el panel`}
            title="Alertas activas"
            className={`inline-flex items-center gap-2 rounded-full text-sm font-extrabold shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#012472] ${compacto ? "px-2.5 py-2" : "px-3.5 py-2.5"} ${pillTone}`}>
            {hayAlta ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
            <span className="tabular-nums">{altaSinTomar.length + mediaSinTomar.length}</span>
            <span className="sr-only">alerta(s) sin tomar · canal {healthMeta.txt}</span>
            {!compacto && altaSinTomar.length > 0 && <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px]">{altaSinTomar.length} ALTA</span>}
            {!compacto && (open ? <ChevronDown className="h-4 w-4 opacity-70" /> : <ChevronUp className="h-4 w-4 opacity-70" />)}
          </button>
          {/* Sólo presentación: compactar NO altera alarmas, conteos, prioridad ni ownership. */}
          <button data-testid="global-alert-compact-toggle"
            onClick={() => { setCompacto((v) => !v); if (!compacto) setOpen(false); }}
            aria-pressed={compacto}
            aria-label={compacto ? "Mostrar el centro de alertas completo" : "Compactar el centro de alertas"}
            title={compacto ? "Mostrar completo" : "Compactar"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-md hover:text-[#012472] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#012472] dark:border-slate-700 dark:bg-slate-800/95 dark:text-slate-300">
            {compacto ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
